"use client";

import type { Dispatch, ReactNode, SetStateAction } from "react";
import { ArrowLeft } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FieldLegend, FieldSet } from "@/components/ui/field";
import { CondensedPhotoUpload } from "@/components/product/condensed-photo-upload";
import { PickupLocationFields } from "@/components/product/pickup-location-fields";
import {
  isValidUsZip,
  type DeliveryLocation,
} from "@/lib/checkout/delivery-location";
import type { Photo } from "@/lib/products/types";
import type { Id } from "@convex/_generated/dataModel";

interface PublicListingBasicsProps {
  quantity: number;
  photos: Photo[];
  heading: ReactNode;
  productSelect?: ReactNode;
  productSlug: string;
  location: DeliveryLocation;
  setLocation: Dispatch<SetStateAction<DeliveryLocation>>;
  onQuantityChange: (value: number) => void;
  onPhotosChange: (photos: Photo[]) => void;
  generateUploadUrl?: () => Promise<string>;
  attestUpload?: (storageId: Id<"_storage">) => Promise<unknown>;
  onBack?: () => void;
  onContinue: () => void;
}

function missingRequirements({
  quantity,
  productSlug,
  location,
  photos,
}: PublicListingBasicsProps) {
  return [
    !productSlug && "choose a packaging type",
    !(Number.isInteger(quantity) && quantity > 0) &&
      "enter a whole-number quantity greater than zero",
    !(isValidUsZip(location.zip) && location.city && location.state) &&
      "confirm the pickup location",
    photos.length === 0 && "add at least one photo",
  ].filter(Boolean);
}

function ListingQuantity({
  quantity,
  productSlug,
  onQuantityChange,
}: Pick<
  PublicListingBasicsProps,
  "quantity" | "productSlug" | "onQuantityChange"
>) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor="listing-quantity">
        {productSlug === "shipping-boxes"
          ? "Number of boxes"
          : "Quantity available"}
      </Label>
      <Input
        id="listing-quantity"
        type="number"
        min={1}
        step={1}
        inputMode="numeric"
        placeholder="e.g. 500"
        value={quantity || ""}
        onChange={(event) => onQuantityChange(Number(event.target.value))} variant="mono"/>
      {productSlug === "shipping-boxes" && (
        <p className="text-xs text-muted-foreground">
          Count individual boxes, including boxes packed in bundles or on
          pallets.
        </p>
      )}
    </div>
  );
}

function ListingBasicsFields(props: PublicListingBasicsProps) {
  return (
    <div className="space-y-4">
      {props.productSelect}
      <ListingQuantity
        quantity={props.quantity}
        productSlug={props.productSlug}
        onQuantityChange={props.onQuantityChange}
      />
      <PickupLocationFields
        idPrefix="pickup"
        location={props.location}
        setLocation={props.setLocation}
      />
      <FieldSet>
        <FieldLegend>Photos (at least 1 required)</FieldLegend>
        <CondensedPhotoUpload
          photos={props.photos}
          onPhotosChange={props.onPhotosChange}
          generateUploadUrl={props.generateUploadUrl}
          attestUpload={props.attestUpload}
        />
      </FieldSet>
    </div>
  );
}

export function PublicListingBasics(props: PublicListingBasicsProps) {
  const requirements = missingRequirements(props);
  const canContinue = requirements.length === 0;
  return (
    <div className="flex flex-1 flex-col">
      {props.heading}
      <div className="mt-4">
        <ListingBasicsFields {...props} />
      </div>
      {!canContinue && (
        <p
          id="listing-required"
          role="status"
          className="mt-4 text-sm text-muted-foreground"
        >
          To continue: {requirements.join("; ")}.
        </p>
      )}
      <div className="flex items-center justify-between gap-3 pt-6">
        {props.onBack && (
          <Button variant="ghost" onClick={props.onBack} className="gap-1.5">
            <ArrowLeft className="size-4" />
            Back
          </Button>
        )}
        <Button
          variant="primary"
          className={props.onBack ? "ml-auto" : "w-full"}
          onClick={props.onContinue}
          disabled={!canContinue}
          aria-describedby={!canContinue ? "listing-required" : undefined}
        >
          Continue
        </Button>
      </div>
    </div>
  );
}
